import type {
	Actions,
	OrderBuyerOutput,
	OrderUnifiedOutput,
	getOneOrderOutput,
} from "@/api/custom_types";
import { useStatusMap } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useActionOnOrder, useOrderAction } from "@/services";
import { currentUserAtom } from "@/store/login";
import {
	ActionItem,
	ActionMenu,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	toast,
} from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import { type FC, type ReactNode, useState } from "react";
import OrdersEditDialog from "./OrdersEditDialog";

export interface OrdersSingleActionProps {
	selectedOrder: OrderUnifiedOutput | getOneOrderOutput;
}

function isBuyerOrder(
	order: OrderUnifiedOutput | getOneOrderOutput,
): order is OrderBuyerOutput {
	return order.schema_type === "for_buyer";
}

interface OrderActionDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	description: ReactNode;
	confirmLabel: string;
	loading: boolean;
	onConfirm: () => void;
}

const OrderActionDialog: FC<OrderActionDialogProps> = ({
	open,
	onOpenChange,
	description,
	confirmLabel,
	loading,
	onConfirm,
}) => {
	const { t } = useTranslation();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("action.areYouSure")}</DialogTitle>
				</DialogHeader>
				<div className="px-4">{description}</div>
				<DialogFooter
					buttons={{
						secondary: {
							label: t("common.button.no"),
							props: {
								type: "button",
								onClick: () => onOpenChange(false),
							},
						},
						primary: {
							label: confirmLabel,
							props: {
								type: "button",
								loading,
								onClick: onConfirm,
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

const OrdersSingleAction: React.FC<OrdersSingleActionProps> = ({
	selectedOrder,
}) => {
	const { t } = useTranslation();
	const user = useAtomValue(currentUserAtom);
	const isAdmin = user?.user_role === "ADMIN";

	const orderAction = useOrderAction();
	const adminActionOnOrder = useActionOnOrder();
	const { orderStatusMap } = useStatusMap();

	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);

	const [playDialog, setPlayDialog] = useState<boolean>(false);
	const [pauseDialog, setPauseDialog] = useState<boolean>(false);
	const [editDialog, setEditDialog] = useState<boolean>(false);
	const [cancelDialog, setCancelDialog] = useState<boolean>(false);

	const handleOrderAction = (action: Actions) => {
		orderAction.mutate(
			{ path: { order_id: selectedOrder.pk }, query: { action } },
			{
				onSuccess: () => {
					switch (action) {
						case "pause":
							toast.success(t("order.action.successPauseOrder"));
							break;
						case "resume":
							toast.success(t("order.action.successResumeOrder"));
							break;
						case "cancel":
							toast.success(t("order.action.successCancelOrder"));
							break;
						default:
							action satisfies never;
					}
					setPauseDialog(false);
					setPlayDialog(false);
					setCancelDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	const handleRefundAction = () => {
		adminActionOnOrder.mutate(
			{
				path: { order_id: selectedOrder.pk },
				body: { action: "REFUND" },
			},
			{
				onSuccess: () => {
					setMenuActionOpen(false);
				},
			},
		);
	};

	const handleTrackingClick = () => {
		setMenuActionOpen(false);
		window.open(
			selectedOrder.tracking_page_url,
			"_blank",
			"noopener,noreferrer",
		);
	};

	const handleEditClick = () => {
		setEditDialog(true);
		setMenuActionOpen(false);
	};

	const statusCaps = orderStatusMap?.[selectedOrder.status];

	const canResume = Boolean(statusCaps?.can_resume);
	const canPause = Boolean(statusCaps?.can_pause);
	const canEdit = Boolean(statusCaps?.can_be_updated);
	const canCancel = Boolean(statusCaps?.can_be_canceled);

	const isRefundDisabled =
		selectedOrder.status === "PROCESSING" ||
		selectedOrder.status === "REFUNDED";

	return (
		<>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible */}
			<div onClick={(event) => event.stopPropagation()}>
				<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
					<ActionItem
						leadingIconName="truck-line"
						onClick={handleTrackingClick}
					>
						{t("action.tracking")}
					</ActionItem>
					<ActionItem
						leadingIconName="pause-circle-line"
						loading={orderAction.isPending}
						disabled={!canPause}
						onClick={() => {
							setMenuActionOpen(false);
							setPauseDialog(true);
						}}
					>
						{t("action.pause")}
					</ActionItem>
					<ActionItem
						leadingIconName="play-circle-line"
						loading={orderAction.isPending}
						disabled={!canResume}
						onClick={() => {
							setMenuActionOpen(false);
							setPlayDialog(true);
						}}
					>
						{t("action.play")}
					</ActionItem>
					{selectedOrder.schema_type === "for_buyer" && (
						<ActionItem
							leadingIconName="edit-line"
							onClick={handleEditClick}
							disabled={!canEdit}
						>
							{t("action.edit")}
						</ActionItem>
					)}
					<ActionItem
						leadingIconName="delete-bin-line"
						loading={orderAction.isPending}
						disabled={!canCancel}
						onClick={() => {
							setMenuActionOpen(false);
							setCancelDialog(true);
						}}
					>
						{t("action.cancel")}
					</ActionItem>
					{isAdmin && (
						<ActionItem
							leadingIconName="refund-2-line"
							loading={adminActionOnOrder.isPending}
							disabled={isRefundDisabled}
							onClick={handleRefundAction}
						>
							{t("admin.orders.refund")}
						</ActionItem>
					)}
				</ActionMenu>
			</div>

			<OrderActionDialog
				open={playDialog}
				onOpenChange={setPlayDialog}
				description={t("order.action.playMessage", { value: 1 })}
				confirmLabel={t("action.button.play")}
				loading={orderAction.isPending}
				onConfirm={() => handleOrderAction("resume")}
			/>

			<OrderActionDialog
				open={pauseDialog}
				onOpenChange={setPauseDialog}
				description={t("order.action.pauseMessage", { value: 1 })}
				confirmLabel={t("action.button.pause")}
				loading={orderAction.isPending}
				onConfirm={() => handleOrderAction("pause")}
			/>

			<OrderActionDialog
				open={cancelDialog}
				onOpenChange={setCancelDialog}
				description={t("order.action.cancelMessage", { value: 1 })}
				confirmLabel={t("action.button.confirmCancel")}
				loading={orderAction.isPending}
				onConfirm={() => handleOrderAction("cancel")}
			/>

			{editDialog && isBuyerOrder(selectedOrder) && (
				<OrdersEditDialog
					key={selectedOrder.pk}
					values={selectedOrder}
					open={true}
					onClose={() => setEditDialog(false)}
				/>
			)}
		</>
	);
};

export default OrdersSingleAction;
